import { Box, Grid } from '@material-ui/core';
import {
  extendDataItem,
  filterBy,
  mapTree,
  orderBy,
  TreeList,
  TreeListDataStateChangeEvent,
} from '@progress/kendo-react-treelist';
import cx from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import { cloneDeep, get, isEmpty, isNil, keyBy, map, omit, replace, uniq } from 'lodash';
import { reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SM } from '../../../../App';
import { translate } from '../../../../common/intl';
import { companySelectors } from '../../../../state/ducks/company';
import DocumentRevisionDialog from '../../../components/common/dialogs/DocumentRevisionDialog';
import ScanCaptureDialog from '../../../components/common/dialogs/scaner';
import { ScanSelectedPartItem } from '../../../components/common/dialogs/scaner/components/lot.table/types';
import { getTableMinWidth } from '../../../components/common/treelist/getTableMinWidth';
import { CustomTreeListColumnProps } from '../../../components/common/treelist/types';
import { Button } from '../../../components/forms/fields-next';
import { MODE_FIELD } from '../../../components/KendoDataGrid/constants';
import KendoDataGridShell, { toKendoDataGridShellColumns } from '../../../components/KendoDataGrid/KendoDataGridShell';
import { toastError } from '../../../components/notifications';
import { useCurrentFormValue } from '../../../hooks/useCurrentFormValue';
import useDialog from '../../../hooks/useDialog';
import { LHRS_ACCEPTED_QUANTITY } from '../../FBLHRStep/utils';
import { findDeviationMatch } from '../../FBProcedureItem/deviation.utils';
import { FBProcedureItemConfig, FBProcedureItemType } from '../../FBProcedureItem/FBProcedureItem.types';
import { FB } from '../../helpers';
import { createMaterialAddItem, isPartHasInsufficientQty, prepareData } from './components/utils';
import {
  ADD_FIELD,
  DATA_ITEM_KEY,
  EB_FIELDS_TO_OMIT,
  EXPAND_FIELD,
  FIELDS_TO_OMIT,
  IS_LOT_ADDITION,
  LHRS_START_QUANTITY_NAME,
  LOT_FIELDS_TO_OMIT,
  SUB_ITEMS_FIELD,
} from './constants';
import { useMaterialActions } from './hooks/useMaterialActions';
import Placeholder from './placeholder';
import { buildEBSchema } from './schemas/ebSchema';
import { buildLHRTSchema } from './schemas/lhrtSchema';
import { buildMPISchema } from './schemas/mpiSchema';
import { buildSchema } from './schemas/schema';
import { getMaterialShellColumns } from './shellColumns';
import useStyles from './styles';
import {
  CustomTreeListExpandChangeEvent,
  CustomTreeListRowProps,
  EditableMaterial,
  Material,
  MaterialEditEvent,
  MaterialItemState,
  MaterialTableProps,
} from './types';
import View from './view';
import { withMaterialsTable } from './wrap';

interface MaterialColumnHandlers {
  onDiscard: (dataItem?: EditableMaterial) => void
  onDelete: (dataItem: EditableMaterial) => void
  addLot: (dataItem: EditableMaterial) => void
  onConfirm: () => void
  onScanClick: (dataItem: EditableMaterial) => void
  onRowClick: (event: MaterialEditEvent) => void
}

interface MaterialTreelistContextProps {
  lhrsQuantity?: number
  addLot?: (dataItem: EditableMaterial) => void
  openQuickView?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, dataItem: EditableMaterial) => void
  isRedlineActive?: boolean
  isDisabled?: boolean
  isEBProcedure?: boolean
  isMPIProcedure?: boolean
  disabledOptions?: string[]
  acceptedQuantity?: number
  allowExpiredLots?: boolean
  shouldEnforceProductionLotsOnly?: boolean
}

export const MaterialTreelistContext = React.createContext<MaterialTreelistContextProps>({
  lhrsQuantity: 1,
  addLot: undefined,
  isRedlineActive: false,
  isDisabled: false,
  isEBProcedure: false,
});

const MaterialsTableInner: React.FC<MaterialTableProps> = ({
  items,
  type,
  saveLot,
  editLot,
  saveScannedLots,
  handleSelectionDelete,
  getItem,
  handleRemove,
  isActive,
  isDesign,
  lhrStepIndex,
  isLHRStep,
  isDisabled,
  isPendingChange,
  isEnableOnlyLotAddition,
  partsSelected,
  isMPIProcedure,
  isPreview,
  isEBProcedure,
  isDeviationMode,
  hideDeviationChanges,
  baseItems,
  startInAddMode = false,
}) => {
  const { _tabsState } = SM.useStores();
  const size = useObserver(() => _tabsState?.contentSize);
  const { procedureState, mpiProcedureState, lhrStepState, workspaceState, procedureCatalogState } = FB.useStores();
  const classes = useStyles({ size });
  const treeListRef = useRef(null);
  const [selectedDataItem, setSelectedDataItem] = useState<EditableMaterial>();
  const quickViewDialog = useDialog();
  const isLHRProductionBuild = workspaceState?.isLHRProductionBuild || false;
  const allowExpiredLots = workspaceState?.lhrBuildTypeConfigForSelectedBuildType?.enforceNonExpiredLots;
  const shouldEnforceProductionLotsOnly = workspaceState?.lhrBuildTypeConfigForSelectedBuildType?.enforceProdLotsOnly;
  const [initialAddItem] = useState<EditableMaterial | undefined>(
    () => startInAddMode ? createMaterialAddItem(isMPIProcedure) : undefined,
  );
  const [state, setState] = React.useState<MaterialItemState>(() => ({
    data: initialAddItem ? [initialAddItem] : prepareData(cloneDeep(items), isDesign, isEBProcedure),
    expanded: [],
    dataState: {
      sort: [],
      filter: [],
    },
    inEdit: initialAddItem ? [initialAddItem] : [],
  }));
  const isRedlineActive = useSelector(companySelectors.getRedlineActive);
  const disabledOptions = useMemo(() => map(items, 'title'), [items]);

  React.useEffect(() => {
    if (!(isMPIProcedure || isLHRStep || isEBProcedure)) {
      return;
    }

    if (isEmpty(items)) {
      setState((prevState) => {
        const hasUnsavedAdd = prevState.data.some((item) => item[ADD_FIELD]);
        if (hasUnsavedAdd) {
          return prevState;
        }
        return {
          ...prevState,
          data: [],
          inEdit: [],
        };
      });
      return;
    }

    setState((prevState) => ({
      ...prevState,
      data: prepareData(cloneDeep(items), isDesign, isEBProcedure),
      inEdit: [],
    }));
  }, [items]);

  const updateState = (stateInfo) => {
    setState(stateInfo);
  };

  const handleExpandChange = (event: CustomTreeListExpandChangeEvent) => {
    const expanded = event.value
      ? state.expanded.filter((id) => id !== event.dataItem.id)
      : uniq([...state.expanded, event.dataItem.id]);

    updateState({
      ...state,
      expanded,
    });
  };

  const saveProcedureItem = (procedureItem: FBProcedureItemConfig) => {
    if (isLHRStep) {
      const updatedItem = omit(procedureItem, ['type', 'LHRTparameter']);
      const schemaItem = workspaceState?.getSchemaItemAt(lhrStepIndex);
      lhrStepState?.editItem(updatedItem, type);
      if (!lhrStepIndex) {
        return;
      }
      if (schemaItem && workspaceState?.schema && workspaceState.schema[lhrStepIndex]) {
        workspaceState.schema[lhrStepIndex] = {
          ...schemaItem,
          editorConfig: {
            ...schemaItem?.editorConfig,
            parameters: {
              ...keyBy(lhrStepState?.editedParameters, 'paramsId'),
            },
          },
        };
      }
      workspaceState?.saveDocRev();
      formik.resetForm();
      return;
    }

    const allSteps = workspaceState?.schema?.filter((item) => item.type === 'mpiprocedure');
    procedureState?.addItem(procedureItem, type, allSteps);
    mpiProcedureState?.addItem(procedureItem, type, allSteps);

    if (isMPIProcedure) {
      return;
    }

    const updatedProcedureItem = {
      ...procedureItem,
      title: procedureItem.title,
    };
    appendItemToStateData(updatedProcedureItem);
  };

  const hasDuplicateLot = (state, values) => {
    const parentIndex = state.data?.findIndex((data) => data.id === values.parentId);
    if (parentIndex === -1) return false;

    const subItems = state.data[parentIndex][SUB_ITEMS_FIELD];
    return subItems?.some((item) => item.lotId === values.lotId && item.id !== values.id);
  };

  const formik = useFormik<Partial<EditableMaterial>>({
    initialValues: initialAddItem ?? {},
    onSubmit: (values) => {
      if (isEnableOnlyLotAddition && !isDeviationMode) {
        if (values[ADD_FIELD]) {
          if (hasDuplicateLot(state, values)) {
            toastError(
              translate('form.builder.lhrs.procedure.select.lot.duplicate.error.message', {
                lotId: values.selectedLotLabel,
              }),
            );
            return;
          }

          saveLot?.(values as EditableMaterial);
        } else {
          editLot?.(values as EditableMaterial);
        }
        discardScanLot(values as EditableMaterial);
        return;
      }

      if (values[IS_LOT_ADDITION] || values.parentPartId) {
        const scanLot = omit(values, LOT_FIELDS_TO_OMIT) as FBProcedureItemConfig;
        const parentIndex = state.data?.findIndex((data) => data.id === values.parentId);

        if (parentIndex !== -1) {
          let subItems = state.data[parentIndex][SUB_ITEMS_FIELD];

          if (Array.isArray(subItems)) {
            const existingSubIndex = subItems.findIndex((item) => item.id === values.id);

            if (existingSubIndex !== -1) {
              const shouldMoveToEnd = isEBProcedure && subItems[existingSubIndex][ADD_FIELD];

              if (shouldMoveToEnd) {
                subItems = subItems.filter((item) => item.id !== values.id);
                subItems.push(scanLot);
              } else {
                subItems[existingSubIndex] = {
                  ...omit(subItems[existingSubIndex], LOT_FIELDS_TO_OMIT),
                  ...scanLot,
                };
              }
            } else {
              subItems.push(scanLot);
            }
            state.data[parentIndex][SUB_ITEMS_FIELD] = subItems.map(
              (item) => omit(item, LOT_FIELDS_TO_OMIT) as unknown as FBProcedureItemConfig,
            );
          } else {
            state.data[parentIndex][SUB_ITEMS_FIELD] = [scanLot];
          }
        }

        let payload = state.data[parentIndex];
        if (isEBProcedure) {
          if (hasDuplicateLot(state, values)) {
            toastError(
              translate('form.builder.lhrs.procedure.select.lot.duplicate.error.message', {
                lotId: values.selectedLotLabel,
              }),
            );
            return;
          }

          payload = omit(payload, EB_FIELDS_TO_OMIT) as FBProcedureItemConfig;
        }

        discardScanLot(values as EditableMaterial);
        const allSteps = workspaceState?.schema?.filter((step) => step.type === 'mpiprocedure');
        procedureState?.addItem(payload as FBProcedureItemConfig, type, allSteps);
        mpiProcedureState?.addItem(payload as FBProcedureItemConfig, type, allSteps);
      } else {
        if (isDesign || isDeviationMode) {
          const procedureItem = { ...omit(values, LOT_FIELDS_TO_OMIT) };
          saveProcedureItem(procedureItem as FBProcedureItemConfig);
          return;
        }

        const omitFields = isEBProcedure ? EB_FIELDS_TO_OMIT : FIELDS_TO_OMIT;
        const scanLot = omit(values, omitFields);
        if (scanLot[SUB_ITEMS_FIELD]) {
          scanLot[SUB_ITEMS_FIELD] = scanLot[SUB_ITEMS_FIELD]?.map(
            (item) => omit(item, LOT_FIELDS_TO_OMIT) as unknown as FBProcedureItemConfig,
          );
        }
        saveProcedureItem(scanLot as FBProcedureItemConfig);
      }
    },
  });

  const addScanLotsToPart = (scanLots) => {
    scanDialog.close();
    const parentIndex = state.data?.findIndex((data) => data.id === scanSelectedPartInfo.current?.id);
    if (parentIndex === -1) {
      return;
    }

    const updatedLots = scanLots.map((lot) => {
      return {
        ...lot,
        parentPartId: scanSelectedPartInfo.current?.title,
      };
    });

    if (isEBProcedure) {
      const newSubItems = scanLots.map((lot) => ({
        ...lot,
        id: FB.uniqid,
      }));

      const parentItem = state.data[parentIndex];

      const updatedSubItems = [...(parentItem[SUB_ITEMS_FIELD] ?? []), ...newSubItems].map((item) =>
        omit(item, [...EB_FIELDS_TO_OMIT, 'title']),
      );

      state.data[parentIndex][SUB_ITEMS_FIELD] = updatedSubItems;
      const payload = omit(parentItem, EB_FIELDS_TO_OMIT) as FBProcedureItemConfig;

      const allSteps = workspaceState?.schema?.filter((step) => step.type === 'mpiprocedure');
      procedureState?.addItem(payload, type, allSteps);
      mpiProcedureState?.addItem(payload, type, allSteps);
      return;
    }

    saveScannedLots?.(updatedLots);
  };

  const openQuickView = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>, dataItem: EditableMaterial) => {
    setSelectedDataItem(dataItem);
    quickViewDialog.open();
    event.preventDefault();
    event.stopPropagation();
  }, [quickViewDialog]);

  const handleDataStateChange = (event: TreeListDataStateChangeEvent) => {
    updateState({
      ...state,
      dataState: event.dataState,
    });
  };

  useEffect(() => {
    if (!partsSelected?.length || !items?.length) {
      return;
    }

    const mapByMaterialId = partsSelected?.reduce((acc, selected) => {
      const materialId = selected?.materialId ?? '';
      if (!acc[materialId]) {
        acc[materialId] = [];
      }
      const index = items?.findIndex((item) => {
        return item.title === selected.materialId;
      });

      const lotItem = {
        id: selected.refId,
        title: '',
        lotId: selected.lotId ?? selected.actualId,
        lotDocId: selected.lotDocId,
        parentPartId: selected.materialId,
        parentId: index !== -1 ? state.data[index].id : '',
        unit: selected.unit,
        quantity: selected.quantity,
        [MODE_FIELD]: false,
      };
      acc[materialId].push(lotItem);
      return acc;
    }, {});

    Object.keys(mapByMaterialId)?.map((materialId) => {
      const index = items?.findIndex((item) => {
        return item.title === materialId;
      });

      if (index !== -1) {
        state.data[index][SUB_ITEMS_FIELD] = mapByMaterialId[materialId];

        updateState({
          ...state,
          data: [...state.data],
        });
      }
    });
  }, [partsSelected]);

  const scanDialog = useDialog();
  const scanSelectedPartInfo = useRef<ScanSelectedPartItem>();

  const openScanDialog = useCallback((dataItem: EditableMaterial) => {
    const plannedIdLabel = procedureCatalogState?.getMaterialLabel(dataItem.title) ?? '';
    const match = /^(.+?) Rev (.+?) - (.+)$/.exec(plannedIdLabel) ?? ['', '', ''];

    scanSelectedPartInfo.current = {
      docId: match[1] ?? '',
      partDescription: match[3],
      unit: dataItem.unit,
      quantityNeeded: dataItem.quantity,
      displayRevision: match[2],
      variance: '',
      partRevId: dataItem.title,
      id: dataItem.id,
      title: dataItem?.title,
      type: FBProcedureItemType.materials,
      existingLotIds: dataItem?.[SUB_ITEMS_FIELD]?.map((data) => get(data, 'lotId')),
    };
    scanDialog.open();
  }, [procedureCatalogState, scanDialog]);

  const { formState } = FB.useStores();
  const acceptedQuantity = useCurrentFormValue<number>(LHRS_ACCEPTED_QUANTITY);
  const [lhrsQuantity, setLhrsQuantity] = useState(1);
  const { appendItemToStateData, removeScanLot, addPartItem, editPartItem, discardScanLot, addLot }
    = useMaterialActions(
      state,
      setState,
      formik,
      {
        isEnableOnlyLotAddition,
        isEBProcedure,
        isDesign,
        isPreview,
        isLHRStep,
        isMPIProcedure,
        isDisabled,
        isDeviationMode,
        type,
        isActive,
        isPendingChange,
        isRedlineActive,
      },
      {
        handleSelectionDelete,
        handleRemove,
        procedureState,
      },
    );

  const columnHandlersRef = useRef<MaterialColumnHandlers>({
    onDiscard: discardScanLot,
    onDelete: removeScanLot,
    addLot,
    onConfirm: formik.submitForm,
    onScanClick: openScanDialog,
    onRowClick: editPartItem,
  });
  columnHandlersRef.current = {
    onDiscard: discardScanLot,
    onDelete: removeScanLot,
    addLot,
    onConfirm: formik.submitForm,
    onScanClick: openScanDialog,
    onRowClick: editPartItem,
  };

  const isLHRT = Boolean(isLHRStep && isMPIProcedure);
  const columns = useMemo<CustomTreeListColumnProps[]>(() => {
    const schemaBuilder = (() => {
      if (isMPIProcedure && !isLHRStep) {
        return buildMPISchema;
      }
      if (isLHRStep) {
        return buildLHRTSchema;
      }
      if (isEBProcedure) {
        return buildEBSchema;
      }
      return buildSchema;
    })();

    return schemaBuilder({
      actionsClass: classes.actionsCell,
      popperHolder: classes.popperHolder,
      onDiscard: (dataItem?: EditableMaterial) => columnHandlersRef.current.onDiscard?.(dataItem),
      onDelete: (dataItem: EditableMaterial) => columnHandlersRef.current.onDelete?.(dataItem),
      addLot: (dataItem: EditableMaterial) => columnHandlersRef.current.addLot?.(dataItem),
      isLHRT,
      isDeviationMode,
      onConfirm: () => {
        columnHandlersRef.current.onConfirm?.();
      },
      isLHRProductionBuild,
      onScanClick: (dataItem: EditableMaterial) => columnHandlersRef.current.onScanClick?.(dataItem),
      onRowClick: (event: MaterialEditEvent) => columnHandlersRef.current.onRowClick?.(event),
    });
  }, [
    classes.actionsCell,
    classes.popperHolder,
    isDeviationMode,
    isEBProcedure,
    isLHRProductionBuild,
    isLHRT,
    isLHRStep,
    isMPIProcedure,
  ]);

  const tableProps = useMemo(() => ({
    style: { tableLayout: 'fixed' as const, minWidth: getTableMinWidth(columns) },
  }), [columns]);

  const rowRender = useCallback((row, props: CustomTreeListRowProps) => {
    const item = props.dataItem as EditableMaterial;
    const isEditMode = (item[MODE_FIELD] || item[ADD_FIELD]);
    const { isInsufficientQty } = (!isEditMode && !isEmpty(item[SUB_ITEMS_FIELD]))
      ? isPartHasInsufficientQty(item, lhrsQuantity, acceptedQuantity)
      : { isInsufficientQty: false };

    const showDeviationDiff = !hideDeviationChanges && baseItems !== undefined && !item.parentPartId;
    const isDeviationRemoved = showDeviationDiff && item.isDeviationRemoved;
    const baseItem = showDeviationDiff && !isDeviationRemoved ? findDeviationMatch(item, baseItems) : undefined;
    const isDeviationAdded = showDeviationDiff && !isDeviationRemoved && !baseItem;
    const isDeviationChanged = Boolean(baseItem && (baseItem.quantity !== item.quantity || baseItem.unit !== item.unit));

    const modifiedProps = {
      className: cx(replace(row?.props?.className, 'k-alt', ''), {
        [classes.whiteBg]: isEmpty(item[SUB_ITEMS_FIELD]),
        [classes.grayBg]: !isEmpty(item[SUB_ITEMS_FIELD]),
        [classes.errorBg]: (!isEmpty(item.type) && isLHRStep && isDesign) || (isInsufficientQty && !isEBProcedure),
        [classes.deviationRemovedBg]: Boolean(isDeviationRemoved),
        [classes.deviationAddedBg]: Boolean(isDeviationAdded) || isDeviationChanged,
      }),
    };

    const showWithoutActions = !item[MODE_FIELD];

    if (showWithoutActions) {
      return React.cloneElement(row, {
        ...modifiedProps,
        ['data-cy' as string]: `alloc-row-${item?.id}`,
      });
    }

    const editedRow = React.cloneElement(row, {
      ...modifiedProps,
      ['data-cy' as string]: `alloc-row-${item?.id}`,
      className: cx(row.props.className, classes.updatingRow),
    });

    return <FormikProvider value={formik}>{editedRow}</FormikProvider>;
  }, [
    acceptedQuantity,
    classes.deviationAddedBg,
    classes.deviationRemovedBg,
    classes.errorBg,
    classes.grayBg,
    classes.updatingRow,
    classes.whiteBg,
    formik,
    isDesign,
    isEBProcedure,
    isLHRStep,
    lhrsQuantity,
    hideDeviationChanges,
    baseItems,
    items,
  ]);

  const cellRender = useCallback((
    tdElement: React.ReactElement<HTMLTableCellElement, string | React.JSXElementConstructor<any>> | null,
  ): React.ReactElement<HTMLTableCellElement> => {
    const props = {
      ...tdElement?.props,
      className: cx(tdElement?.props.className, classes.borderBottom),
    };
    const defaultRendering = { ...tdElement, props: props };
    return defaultRendering as React.ReactElement<HTMLTableCellElement>;
  }, [classes.borderBottom]);

  // Materials the deviation deleted: shown read-only (in red) while "Show Deviation Changes" is on
  const deviationRemovedItems = useMemo(() => {
    if (hideDeviationChanges || !baseItems) {
      return [];
    }
    return baseItems
      .filter((i) => !findDeviationMatch(i, items))
      .map((i) => ({ ...omit(i, SUB_ITEMS_FIELD), isDeviationRemoved: true }) as EditableMaterial);
  }, [hideDeviationChanges, baseItems, items]);

  const treeListData = useMemo(() => {
    const { data, dataState, expanded, inEdit } = state;
    if (!dataState) {
      return [];
    }

    const filteredData = dataState.filter && filterBy(data, [...dataState.filter], SUB_ITEMS_FIELD);
    const sortedData = dataState.sort && filteredData && orderBy(filteredData, dataState?.sort, SUB_ITEMS_FIELD);

    return [
      ...mapTree(sortedData as Material[], SUB_ITEMS_FIELD, (item) =>
        extendDataItem(item, SUB_ITEMS_FIELD, {
          [EXPAND_FIELD]: expanded.includes(item.id),
          [MODE_FIELD]: Boolean(inEdit.find((i) => i.id === item.id)),
        }),
      ),
      ...deviationRemovedItems,
    ];
  }, [state, deviationRemovedItems]);

  const treeListStyle = useMemo(() => ({
    overflow: 'auto' as const,
    maxHeight: size?.height ? `${size.height}px` : '100px',
  }), [size?.height]);

  const isContextDisabled = isDeviationMode ? false : isEBProcedure ? isDisabled : isDesign || !isEnableOnlyLotAddition;
  const addContextLot = useCallback((dataItem: EditableMaterial) => columnHandlersRef.current.addLot(dataItem), []);
  const contextValue = useMemo(() => ({
    lhrsQuantity,
    addLot: addContextLot,
    openQuickView,
    isRedlineActive,
    isDisabled: isContextDisabled,
    isEBProcedure,
    disabledOptions,
    isMPIProcedure,
    acceptedQuantity,
    allowExpiredLots,
    shouldEnforceProductionLotsOnly,
  }), [
    acceptedQuantity,
    addContextLot,
    allowExpiredLots,
    disabledOptions,
    isContextDisabled,
    isEBProcedure,
    isMPIProcedure,
    isRedlineActive,
    lhrsQuantity,
    openQuickView,
    shouldEnforceProductionLotsOnly,
  ]);

  React.useEffect(() => {
    const formValue = cloneDeep(formState?.getFieldValue(LHRS_START_QUANTITY_NAME));
    if (!isNil(formValue)) {
      setLhrsQuantity(formValue);
    }

    const dispose = reaction(
      () => formState?.getInputState(LHRS_START_QUANTITY_NAME)?.value,
      () => {
        const newValue = cloneDeep(formState?.getFieldValue(LHRS_START_QUANTITY_NAME));
        if (!isNil(newValue)) {
          setLhrsQuantity(newValue);
        }
      },
    );

    return () => dispose();
  }, [formState]);

  const canAddNewItem = isDeviationMode || (isActive && !isDisabled && !isLHRStep && (isDesign || !isEnableOnlyLotAddition));
  const isEmptyTable = isEmpty(state.data) && isEmpty(deviationRemovedItems);

  return (
    <Box className={classes.root} data-cy="material-grid">
      <MaterialTreelistContext.Provider value={contextValue}>
        <Grid item xs={12} ref={treeListRef}>
          {isEmptyTable ? (
            <KendoDataGridShell
              columns={toKendoDataGridShellColumns(columns)}
              withBottomButton={canAddNewItem}
              dataCy="material-grid-empty"
            />
          ) : (
            <TreeList
              style={treeListStyle}
              tableProps={tableProps}
              {...state.dataState}
              className={cx(classes.treeList, 'containing-box-scrollbar', {
                [classes.treeListWithBottomButton]: canAddNewItem,
              })}
              data={treeListData}
              dataItemKey={DATA_ITEM_KEY}
              subItemsField={SUB_ITEMS_FIELD}
              expandField={EXPAND_FIELD}
              editField={MODE_FIELD}
              rowHeight={50}
              onExpandChange={handleExpandChange}
              onDataStateChange={handleDataStateChange}
              onRowClick={editPartItem}
              rowRender={rowRender}
              cellRender={cellRender}
              columns={columns}
              resizable
            />
          )}
          {canAddNewItem && (
            <Button kind="add" fullWidth attached onClick={addPartItem} data-cy="add-material">
              {translate('form.builder.procedure.material.table.add.part')}
            </Button>
          )}
          <DocumentRevisionDialog
            dialog={quickViewDialog}
            {...{
              docRevId: selectedDataItem?.lotId,
              parentRevId: selectedDataItem?.parentPartId,
            }}
          />
        </Grid>
      </MaterialTreelistContext.Provider>
      {scanDialog.isOpen && (
        <ScanCaptureDialog
          handler={scanDialog}
          isLHRScan
          onClose={scanDialog.close}
          addScanLotsToPart={addScanLotsToPart}
          selectedPart={scanSelectedPartInfo.current as ScanSelectedPartItem}
          parentRevId={workspaceState?.document?.id}
        />
      )}
    </Box>
  );
};

const MaterialsTableLoaded = memo(withMaterialsTable(MaterialsTableInner));

const MaterialsTable: React.FC<MaterialTableProps> = (props) => {
  const {
    items,
    isPreview,
    isDisabled,
    isMPIProcedure,
    isLHRStep,
    isEBProcedure,
    isActive,
    isDesign,
    isEnableOnlyLotAddition,
    isDeviationMode,
  } = props;
  const isRedlineActive = useSelector(companySelectors.getRedlineActive);
  const [isAdding, setIsAdding] = useState(false);
  const isEmptyItems = isEmpty(items);
  if (!isEmptyItems && isAdding) {
    setIsAdding(false);
  }
  const isViewOnly = Boolean((isPreview || isDisabled) && !isRedlineActive);
  const isMpiForm = Boolean(isMPIProcedure && !isLHRStep);

  // A deviation edits the MPI's materials (the deviation document itself is always an output/preview)
  if (isViewOnly && isMpiForm && !isDeviationMode) {
    return <View items={items} />;
  }

  if (isEmptyItems && !isAdding) {
    const canAdd = Boolean(isDeviationMode)
      || Boolean(isActive && !isDisabled && !isLHRStep && (isDesign || !isEnableOnlyLotAddition));

    return (
      <Placeholder
        columns={getMaterialShellColumns({ isMPIProcedure, isLHRStep, isEBProcedure })}
        canAdd={canAdd}
        onAdd={() => setIsAdding(true)}
      />
    );
  }

  return <MaterialsTableLoaded {...props} startInAddMode={isAdding} />;
};

export default memo(MaterialsTable);
